import { useCallback, useEffect, useRef, useState } from "react";
import {
  FALLBACK_POLL_MS, VIEWING_PING_MS, adminListRooms, fetchThread, markRead, setViewing, subscribeOpsChat,
} from "../lib/opsChat";

/**
 * 운영팀 대화방 — 어드민 화면 공용 훅 (2026-11-03)
 *   「대화방 관리」 화면(AdminOpsChat)과 어드민 어디서나 떠 있는 대화창(AdminOpsChatDock)이 같은 규칙으로 읽는다.
 *   표는 직접 읽지 않고 RPC 로만(src/lib/opsChat.js).
 */

/** 방 목록 — 운영팀 Realtime 신호(ops_chat:staff)로 바로, 끊기면 30초마다 다시 읽는다 */
export function useAdminOpsRoomList({ filter = "open", q = "", enabled = true, onError } = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const load = useCallback(async ({ quiet } = {}) => {
    if (!enabled) return;
    if (!quiet) setLoading(true);
    try {
      const data = await adminListRooms({
        status: filter === "closed" ? "closed" : filter === "waiting" ? "waiting" : "open", mine: filter === "mine", q,
      });
      setRows(data);
      setError(null);
    } catch (err) {
      setError(err);
      if (!quiet) onErrorRef.current?.(err);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [filter, q, enabled]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!enabled) return undefined;
    const iv = setInterval(() => load({ quiet: true }), FALLBACK_POLL_MS);
    const off = subscribeOpsChat("ops_chat:staff", () => load({ quiet: true }));
    return () => { clearInterval(iv); off(); };
  }, [load, enabled]);

  return { rows, loading, error, reload: load };
}

/**
 * 대화 한 방 — 방 채널 신호로 바로 다시 읽고, 새 메시지가 오면 읽음 처리한다.
 * 보는 동안은 30초마다 「보고 있는 중」(그 방 푸시를 건너뜀). onNewMessages 는 메시지 수가 바뀔 때.
 */
export function useAdminOpsRoom(roomId, { errorText, onNewMessages } = {}) {
  const [thread, setThread] = useState(null);
  const [error, setError] = useState(null);
  const lastCount = useRef(0);
  const cbRef = useRef({ errorText, onNewMessages });
  cbRef.current = { errorText, onNewMessages };

  const load = useCallback(async () => {
    if (!roomId) return;
    try {
      const t = await fetchThread(roomId);
      setThread(t);
      setError(null);
      const n = (t?.messages || []).length;
      if (n !== lastCount.current) {
        lastCount.current = n;
        markRead(roomId).catch(() => {});
        cbRef.current.onNewMessages?.();
      }
    } catch (err) {
      setError(cbRef.current.errorText ? cbRef.current.errorText(err) : String(err?.message || err));
    }
  }, [roomId]);

  useEffect(() => {
    lastCount.current = 0;
    setThread(null);
    setError(null);
  }, [roomId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!roomId) return undefined;
    const iv = setInterval(() => { if (document.visibilityState === "visible") load(); }, FALLBACK_POLL_MS);
    const off = subscribeOpsChat(`ops_chat:room:${roomId}`, () => load());
    // 운영팀도 방을 보는 동안은 그 방 푸시를 건너뛴다
    const ping = () => { if (document.visibilityState === "visible") setViewing(roomId, true).catch(() => {}); };
    ping();
    const pingIv = setInterval(ping, VIEWING_PING_MS);
    return () => { clearInterval(iv); clearInterval(pingIv); off(); setViewing(roomId, false).catch(() => {}); };
  }, [load, roomId]);

  return { thread, error, reload: load };
}
